import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Card,
  Upload,
  Button,
  Badge,
  Image,
  message,
  Tooltip,
  Modal,
  Typography,
  Row,
  Col,
  Tabs,
  Space,
  Drawer,
  Slider,
  ColorPicker,
  Select,
  Divider,
  Input,
  Segmented
} from 'antd';
import {
  UploadOutlined,
  CloudOutlined,
  CameraOutlined,
  DeleteOutlined,
  EyeOutlined,
  DragOutlined,
  PictureOutlined,
  EditOutlined,
  ScissorOutlined,
  BgColorsOutlined,
  UndoOutlined,
  CloseOutlined
} from '@ant-design/icons';
import ImageEditorModal from './ImageGallery/ImageEditing';
import { storeImageLocally, updateImageInModule } from '@/stores/slice/reportSlice/inspectionSlice';
import { useDispatch } from 'react-redux';
import InspectionBtn from '@/components/InspectionBtn';

const { Text } = Typography;

// Image Editor Component

const ImageGallery = ({
  gallery = {},
  modules = [],
  onUpload,
  onCameraCapture,
  onDragStart,
  isImageAssigned,
  isVisible = true,
  onDeleteImage,
  showDeleteButton = false,
  currentStepName = null,
  onMobileDrop,
  onImageEdit
}) => {
  const [activeTab, setActiveTab] = useState('general');
  const [cameraModalVisible, setCameraModalVisible] = useState(false);
  const [uploadingModule, setUploadingModule] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerHeight, setDrawerHeight] = useState(75); // Adjustable drawer height
  const dispatch = useDispatch()
  // Image editor state
  const [editorVisible, setEditorVisible] = useState(false);
  const [editingImage, setEditingImage] = useState(null);

  // Touch drag state
  const [isDragging, setIsDragging] = useState(false);
  const [draggedImage, setDraggedImage] = useState(null);
  const [touchOffset, setTouchOffset] = useState({ x: 0, y: 0 });
  const [dragPreview, setDragPreview] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const dragPreviewRef = useRef(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Enhanced touch drag implementation
  useEffect(() => {
    if (!isMobile || !isDragging) return;

    const handleTouchMove = (e) => {
      if (!isDragging || !draggedImage) return;

      e.preventDefault();
      e.stopPropagation();

      const touch = e.touches[0];
      if (dragPreviewRef.current) {
        dragPreviewRef.current.style.left = `${touch.clientX - touchOffset.x}px`;
        dragPreviewRef.current.style.top = `${touch.clientY - touchOffset.y}px`;
      }

      // Hide drawer content during drag to access drop targets
      const drawer = document.querySelector('.ant-drawer-content');
      if (drawer) {
        drawer.style.pointerEvents = 'none';
        drawer.style.opacity = '0.3';
      }
    };

    const handleTouchEnd = (e) => {
      if (!isDragging || !draggedImage) return;

      e.preventDefault();
      e.stopPropagation();

      const touch = e.changedTouches[0];

      // Re-enable drawer
      const drawer = document.querySelector('.ant-drawer-content');
      if (drawer) {
        drawer.style.pointerEvents = 'auto';
        drawer.style.opacity = '1';
      }

      // Find drop target
      const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);

      let dropTarget = elementBelow;
      while (dropTarget && !dropTarget.dataset.dropTarget) {
        dropTarget = dropTarget.parentElement;
      }

      if (dropTarget && dropTarget.dataset.dropTarget) {
        try {
          const dropData = JSON.parse(dropTarget.dataset.dropTarget);

          if (onMobileDrop) {
            onMobileDrop(
              draggedImage,
              dropData.stepIndex,
              dropData.unitIndex,
              dropData.componentName,
              dropData.isCommon
            );
            toast.success('Image assigned successfully!');
          }
        } catch (error) {
          console.error('Drop error:', error);
          toast.error('Failed to assign image');
        }
      } else {
        message.info('No valid drop target found');
      }

      // Clean up
      cleanupDrag();
    };

    // Attach global listeners
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);

      // Ensure drawer is re-enabled
      const drawer = document.querySelector('.ant-drawer-content');
      if (drawer) {
        drawer.style.pointerEvents = 'auto';
        drawer.style.opacity = '1';
      }
    };
  }, [isDragging, draggedImage, touchOffset, onMobileDrop]);

  const cleanupDrag = () => {
    setIsDragging(false);
    setDraggedImage(null);

    if (dragPreviewRef.current && document.body.contains(dragPreviewRef.current)) {
      document.body.removeChild(dragPreviewRef.current);
      dragPreviewRef.current = null;
      setDragPreview(null);
    }

    // Re-enable drawer
    const drawer = document.querySelector('.ant-drawer-content');
    if (drawer) {
      drawer.style.pointerEvents = 'auto';
      drawer.style.opacity = '1';
    }
  };

  if (!isVisible) return null;

  // Get gallery for a specific module
  const getModuleGallery = (moduleKey) => {
    if (moduleKey === 'general') {
      if (Array.isArray(gallery)) {
        return gallery;
      }
      return gallery.general || [];
    }
    return gallery[moduleKey] || [];
  };

  // Handle file upload with proper module targeting
  const handleUpload = async ({ fileList }, targetModule = 'general') => {
    try {
      setUploadingModule(targetModule);
      const actualFiles = fileList
        .map(fileItem => fileItem.originFileObj || fileItem.file || fileItem)
        .filter(file => file instanceof File);

      if (actualFiles.length === 0) {
        toast.error('No valid files selected');
        return;
      }

      await onUpload(actualFiles, targetModule);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Failed to process images: " + (error.message || error));
    } finally {
      setUploadingModule(null);
    }
  };

  // Camera functions
  const startCamera = async (targetModule = 'general') => {
    try {
      setUploadingModule(targetModule);
      setCameraModalVisible(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Failed to access camera');
      setCameraModalVisible(false);
      setUploadingModule(null);
    }
  };

  const capturePhoto = async () => {
    try {
      if (!videoRef.current || !canvasRef.current) return;

      const canvas = canvasRef.current;
      const video = videoRef.current;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      canvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
          await onCameraCapture(file, uploadingModule);

          const moduleDisplayName = uploadingModule === 'general' ? 'General Gallery' : uploadingModule;
          toast.success(`Photo captured and added to ${moduleDisplayName}`);
        }
      }, 'image/jpeg', 0.8);

      stopCamera();
    } catch (error) {
      console.error('Capture error:', error);
      toast.error('Failed to capture photo');
    }
  };


  const handleImageEdit = async (editedFile, originalImageId, moduleKey) => {
    try {
      // Generate new ID for the edited image
      const newImageId = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Store the edited image locally
      const editedImageData = await storeImageLocally(editedFile, newImageId);

      // Update Redux state - replace the original image with the edited one
      dispatch(updateImageInModule({
        module: moduleKey || 'general',
        imageId: originalImageId,
        updatedImageData: {
          ...editedImageData,
          editedAt: new Date().toISOString(),
          originalImageId: originalImageId,
        }
      }));


    } catch (error) {
      console.error('Error handling image edit:', error);
      throw error;
    }
  };

  const handleSaveEdit = async (editedFile, originalImageId, moduleKey) => {
    try {
      await handleImageEdit(editedFile, originalImageId, moduleKey);
      toast.success('Image edited successfully!');
      setEditorVisible(false);
      setEditingImage(null);
    } catch (error) {
      console.error('Edit save error:', error);
      toast.error('Failed to save edited image');
    }
  };



  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraModalVisible(false);
    setUploadingModule(null);
  };

  // Image editing functions
  const handleEditImage = (image, moduleKey) => {
    // Ensure we have proper image data
    const imageToEdit = {
      ...image,
      moduleKey,
      // Ensure URL is properly formatted
      url: image.url || image.src || image.preview
    };

    setEditingImage(imageToEdit);
    setEditorVisible(true);
  };


  // Handle desktop drag start
  const handleDragStart = (e, image, moduleKey) => {

    e.dataTransfer.effectAllowed = "move";

    const dragData = {
      imageId: image.id,
      sourceModule: moduleKey,
      imageData: {
        id: image.id,
        url: image.url,
        name: image.name,
        uploaded: image.uploaded || true
      }
    };

    try {
      e.dataTransfer.setData('application/json', JSON.stringify(dragData));
      e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
    } catch (error) {
      console.error('Error setting drag data:', error);
    }

    if (onDragStart) {
      onDragStart(e, image);
    }
  };

  // Handle mobile touch start (drag initiation)
  const handleTouchStart = (e, image, moduleKey) => {



    // Prevent default touch behavior only for drag
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();

    // Calculate offset from touch point to element origin
    const offsetX = touch.clientX - rect.left;
    const offsetY = touch.clientY - rect.top;

    setTouchOffset({ x: offsetX, y: offsetY });
    setDraggedImage({
      id: image.id,
      url: image.url,
      name: image.name,
      uploaded: image.uploaded || true,
      sourceModule: moduleKey
    });

    // Create drag preview element
    const preview = document.createElement('div');
    preview.style.position = 'fixed';
    preview.style.left = `${touch.clientX - offsetX}px`;
    preview.style.top = `${touch.clientY - offsetY}px`;
    preview.style.width = '80px';
    preview.style.height = '80px';
    preview.style.zIndex = '99999';
    preview.style.pointerEvents = 'none';
    preview.style.opacity = '0.8';
    preview.style.borderRadius = '8px';
    preview.style.border = '2px solid #1890ff';
    preview.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
    preview.style.transform = 'rotate(5deg)';
    preview.style.transition = 'none';
    preview.style.backgroundColor = 'white';

    const img = document.createElement('img');
    img.src = image.url;
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    img.style.borderRadius = '6px';

    preview.appendChild(img);
    document.body.appendChild(preview);

    dragPreviewRef.current = preview;
    setDragPreview(preview);

    // Start drag after a short delay
    setTimeout(() => {
      setIsDragging(true);
      message.info({
        content: 'Dragging image - drop on target field',
        duration: 2
      });
    }, 200);
  };

  // Render individual gallery item
  const renderGalleryItem = (image, moduleKey) => {
    const isAssigned = isImageAssigned && isImageAssigned(image.id);

    return (
      <div
        key={image.id}
        draggable={true}
        className={`gallery-item ${isAssigned ? 'assigned' : 'available'} ${isMobile ? 'mobile-item' : ''}`}
        onDragStart={(e) => handleDragStart(e, image, moduleKey)}
        onTouchStart={(e) => handleTouchStart(e, image, moduleKey)}
        style={{
          flex: "0 0 auto",
          width: "200px",
          height: isMobile ? "100px" : "120px",
          border: isAssigned ? "2px solid #52c41a" : "1px solid #d9d9d9",
          borderRadius: "8px",
          padding: "2px",
          marginRight: "8px",
          marginBottom: "8px",
          backgroundColor: isAssigned ? "#f6ffed" : "#fff",
          cursor: "grab",
          opacity: isAssigned ? 0.7 : 1,
          position: "relative",
          transition: "all 0.3s ease",
          boxShadow: isAssigned ? "0 2px 8px rgba(82, 196, 26, 0.2)" : "0 1px 3px rgba(0,0,0,0.1)",
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
        title={isAssigned ? "This image is already assigned" : "Drag to assign this image"}
      >
        <Image
          src={image.url}
          alt={image.name}
          style={{
            objectFit: "cover",
            borderRadius: "4px",
            pointerEvents: 'none'
          }}
          preview={{
            mask: <div style={{ fontSize: isMobile ? "8px" : "10px" }}><EyeOutlined /> Preview</div>
          }}
        />

        {/* Action buttons row */}
        <div style={{
          position: 'absolute',
          bottom: '2px',
          left: '2px',
          right: '2px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '2px',
        }}>
          {/* Edit button */}
          <Tooltip title="Edit image">
            <Button
              type="text"
              icon={<EditOutlined style={{ fontSize: "16px", color: "white" }} />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleEditImage(image, moduleKey);
              }}
              style={{
                backgroundColor: 'rgba(24, 144, 255, 0.1)',
                border: 'none',
                width: '18px',
                height: '18px',
                minWidth: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                borderRadius: '4px'
              }}
            />
          </Tooltip>

          {/* Delete button */}
          {showDeleteButton && onDeleteImage && (
            <Tooltip title="Delete image">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined style={{ fontSize: "16px" }} />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDeleteImage(image.id, moduleKey);
                }}
                style={{
                  backgroundColor: 'rgba(255, 77, 79, 0.1)',
                  border: 'none',
                  width: '18px',
                  height: '18px',
                  minWidth: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  borderRadius: '4px'
                }}
              />
            </Tooltip>
          )}
        </div>

        {/* Drag indicator */}
        <div style={{
          position: 'absolute',
          top: '2px',
          left: '2px',
          backgroundColor: 'rgba(24, 144, 255, 0.8)',
          color: 'white',
          padding: '1px 4px',
          borderRadius: '4px',
          fontSize: '8px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          pointerEvents: 'none'
        }}>
          <DragOutlined style={{ fontSize: '8px' }} />
          {isMobile ? 'HOLD' : 'DRAG'}
        </div>




      </div>
    );
  };

  // Render gallery content for a specific module
  const renderGalleryContent = (moduleKey) => {
    const moduleImages = getModuleGallery(moduleKey);
    const isUploading = uploadingModule === moduleKey;
    const moduleDisplayName = moduleKey === 'general' ? 'General Gallery' : moduleKey;

    return (
      <div>
        {/* Upload controls */}
        <Row justify="space-between" align="middle" style={{ marginBottom: '12px' }}>
          <Col>
            <Badge count={moduleImages.length} size='small' showZero>
              <Text strong style={{ fontSize: isMobile ? '12px' : '14px' }}>
                {moduleDisplayName}
              </Text>
            </Badge>
          </Col>
          <Col>
            <Space size="small">
              <Upload
                multiple
                showUploadList={false}
                beforeUpload={() => false}
                onChange={(info) => handleUpload(info, moduleKey)}
                accept="image/*"
                disabled={isUploading}
              >

                <InspectionBtn
                  variant="primary"
                  size="xs"
                  loading={isUploading}
                  onClick={() => startCamera(moduleKey)}
                  icon={<UploadOutlined />}
                >
                  Upload
                </InspectionBtn>
              </Upload>
              <InspectionBtn
                variant="primary"
                size="xs"
                disabled={isUploading}
                onClick={() => startCamera(moduleKey)}
                icon={<CameraOutlined />}
              >
                Camera
              </InspectionBtn>


            </Space>
          </Col>
        </Row>

        {/* Images display */}
        {moduleImages.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "#999",
              padding: isMobile ? "15px 0" : "20px 0",
              backgroundColor: "#fafafa",
              borderRadius: "8px",
              border: "2px dashed #d9d9d9"
            }}
          >
            <CloudOutlined style={{ fontSize: isMobile ? "24px" : "32px", marginBottom: "8px" }} />
            <div style={{ fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>
              No images in {moduleDisplayName}
            </div>
            <div style={{ fontSize: isMobile ? "10px" : "12px", color: "#8c8c8c" }}>
              Upload images or capture photos.
              {isMobile ? ' Hold and drag them to form fields after upload.' : ' Drag them to form fields after upload.'}
            </div>
          </div>
        ) : (
          <div
            className="flex"
            style={{
              gap: isMobile ? "4px" : "8px",
              flexWrap: "wrap",
              overflowY: "auto",
              maxHeight: isMobile ? "350px" : "250px",
              paddingBottom: "8px",
              touchAction: 'pan-y'
            }}
          >
            {moduleImages.map(image => renderGalleryItem(image, moduleKey))}
          </div>
        )}
      </div>
    );
  };

  // Create tab items
  // const tabItems = [
  //   {
  //     key: 'general',
  //     label: (
  //       <span style={{ fontSize: isMobile ? '12px' : '14px' }}>
  //         General
  //         <Badge count={getModuleGallery('general').length} size="small" style={{ marginLeft: 4 }} />
  //       </span>
  //     ),
  //     children: renderGalleryContent('general')
  //   },
  //   ...modules.map(module => ({
  //     key: module.name,
  //     label: (
  //       <span style={{ fontSize: isMobile ? '12px' : '14px' }}>
  //         {module.name}
  //         <Badge count={getModuleGallery(module.name).length} size="small" style={{ marginLeft: 4 }} />
  //       </span>
  //     ),
  //     children: renderGalleryContent(module.name)
  //   }))
  // ];


   const segmentedOptions = [
    {
      label: (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '4px' : '6px',
          fontSize: isMobile ? '11px' : '13px',
          fontWeight: 500
        }}>
          General
          <Badge 
            count={getModuleGallery('general').length} 
            size="small" 
            style={{ 
              fontSize: isMobile ? '10px' : '12px',
              minWidth: isMobile ? '16px' : '18px',
              height: isMobile ? '16px' : '18px',
              lineHeight: isMobile ? '14px' : '16px'
            }} 
          />
        </div>
      ),
      value: 'general'
    },
    ...modules.map(module => ({
      label: (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '4px' : '6px',
          fontSize: isMobile ? '11px' : '13px',
          fontWeight: 500
        }}>
          {isMobile ? module.name.slice(0, 8) + (module.name.length > 8 ? '...' : '') : module.name}
          <Badge 
            count={getModuleGallery(module.name).length} 
            size="small" 
            style={{ 
              fontSize: isMobile ? '10px' : '12px',
              minWidth: isMobile ? '16px' : '18px',
              height: isMobile ? '16px' : '18px',
              lineHeight: isMobile ? '14px' : '16px'
            }} 
          />
        </div>
      ),
      value: module.name
    }))
  ];
  // Get total image count for mobile button
  const getTotalImageCount = () => {
    return tabItems.reduce((total, tab) => {
      const moduleImages = getModuleGallery(tab.key);
      return total + moduleImages.length;
    }, 0);
  };


  return (
    <>
      <Card
        size="small"
        title="Image Gallery"
        style={{

          border: "1px solid #e8e8e8"
        }}
        bodyStyle={{
          fontSize: "12px",
          padding: "12px"
        }}
      >
  <Segmented
          value={activeTab}
          onChange={setActiveTab}
          options={segmentedOptions}
          size="small"
          style={{
            backgroundColor: '#f0f2f5',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '1px',
            width: '100%',
            display: 'flex'
          }}
          className="custom-segmented"
        />
         <div className="p-2 mt-2" style={{ 
        backgroundColor: '#fafbfc', 
        borderRadius: '6px',
        minHeight: '200px',
        border: '1px solid #f0f0f0'
      }}>
        {renderGalleryContent(activeTab)}
      </div>
 
      </Card>

      {/* Image Editor Modal */}
      <ImageEditorModal
        visible={editorVisible}
        image={editingImage}
        onSave={handleSaveEdit}
        onCancel={() => {
          setEditorVisible(false);
          setEditingImage(null);
        }}
      />

      {/* Camera Modal for Desktop */}
      <Modal
        title={
          <div className='font-12 flex align-middle gap-2'  >
            <CameraOutlined />
            Capture Photo for {uploadingModule === 'general' ? 'General Gallery' : uploadingModule}
          </div>
        }
        open={cameraModalVisible}
        onCancel={stopCamera}
        footer={[
          <Button key="cancel" onClick={stopCamera}>
            Cancel
          </Button>,
          <Button key="capture" type="primary" onClick={capturePhoto} icon={<CameraOutlined />}>
            Capture Photo
          </Button>
        ]}
        width={650}
        destroyOnClose
      >
        <div style={{ textAlign: 'center' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{
              width: '100%',
              maxWidth: '500px',
              height: 'auto',
              borderRadius: '8px',
              backgroundColor: '#000',
              border: '2px solid #d9d9d9'
            }}
          />
          <canvas
            ref={canvasRef}
            style={{ display: 'none' }}
          />
          <div style={{ marginTop: '16px' }}>
            <Text type="secondary">
              Capturing for: <Badge status="processing" text={uploadingModule === 'general' ? 'General Gallery' : uploadingModule} />
            </Text>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ImageGallery;